/**
 * Shared visual metadata for Defense Technology Intelligence graph nodes:
 * per-type color, icon, shape and sizing so node types are immediately
 * distinguishable across the canvas, legend, clusters and detail panels.
 */
import { IntelNodeType } from '../../types/intelligenceGraph';

export interface NodeTypeMeta {
  label: string;
  color: string;
  icon: string;
  /** Canvas glyph shape. */
  shape: 'circle' | 'square' | 'diamond' | 'hexagon';
  baseRadius: number;
}

export const NODE_TYPE_META: Record<IntelNodeType, NodeTypeMeta> = {
  organization: {
    label: 'Organization',
    color: '#6366f1',
    icon: 'fa6-solid:building',
    shape: 'square',
    baseRadius: 9,
  },
  pla: {
    label: 'PLA / Military',
    color: '#dc2626',
    icon: 'mdi:shield-star',
    shape: 'diamond',
    baseRadius: 10,
  },
  person: {
    label: 'Person',
    color: '#10b981',
    icon: 'ic:baseline-person',
    shape: 'circle',
    baseRadius: 6,
  },
  research: {
    label: 'Research',
    color: '#f59e0b',
    icon: 'mdi:newspaper-variant-outline',
    shape: 'circle',
    baseRadius: 6,
  },
  technology: {
    label: 'Technology',
    color: '#06b6d4',
    icon: 'fa6-solid:microchip',
    shape: 'hexagon',
    baseRadius: 7,
  },
  procurement: {
    label: 'Procurement',
    color: '#f97316',
    icon: 'mdi:file-document-outline',
    shape: 'square',
    baseRadius: 6,
  },
  buyer: {
    label: 'Buyer',
    color: '#0d9488',
    icon: 'mdi:office-building-marker-outline',
    shape: 'square',
    baseRadius: 8,
  },
  supplier: {
    label: 'Supplier',
    color: '#8b5cf6',
    icon: 'mdi:factory',
    shape: 'square',
    baseRadius: 8,
  },
  agency: {
    label: 'Agency',
    color: '#64748b',
    icon: 'mdi:handshake-outline',
    shape: 'square',
    baseRadius: 7,
  },
  email: {
    label: 'Email',
    color: '#a855f7',
    icon: 'mdi:email-outline',
    shape: 'circle',
    baseRadius: 5,
  },
  domain: {
    label: 'Domain',
    color: '#0ea5e9',
    icon: 'mdi:web',
    shape: 'circle',
    baseRadius: 5,
  },
};

/** Root/search node is always the largest; sizes are bounded so high-degree
 * nodes cannot overwhelm the visualization. */
export const ROOT_RADIUS = 16;
export const MAX_RADIUS = 13;

export function nodeRadius(
  type: IntelNodeType,
  recordCount: number,
  isRoot?: boolean,
): number {
  if (isRoot) return ROOT_RADIUS;
  const base = NODE_TYPE_META[type].baseRadius;
  return Math.min(MAX_RADIUS, base + Math.log2(1 + Math.max(recordCount, 0)));
}
